<template>
	<div class="personal-info">
		<div class="personal-info__body">
			<div class="personal-info__column">
				<div class="personal-info__main">
					<div class="personal-info__title _md">
						{{ $t('Basic Information') }}
					</div>
					<form class="personal-info__form" @submit.prevent="updateEmail">
						<BaseInput
							class="personal-info__input"
							v-model="personalInfo.email"
							type="email"
							:label="`${$t('Email')}:`"
						/>
						<div class="personal-info__wrapper">
							<div v-if="personalInfoError" class="personal-info__error">
								{{ personalInfoError }}
							</div>
							<div
								v-if="personalInfoSuccess"
								class="personal-info__success"
							>
								{{ personalInfoSuccess }}
							</div>
							<BaseButton
								class="personal-info__submit _btn-bordered"
								type="submit"
								:disabled="isSubmitting || !personalInfo.email"
							>
								<span v-if="isSubmitting">{{ $t('Changing') }}...</span>
								<span v-else>{{ $t('Change email') }}</span>
							</BaseButton>
						</div>
					</form>
					<ChangePassword />
				</div>
				<div class="personal-info__security security">
					<div class="security__title _md">
						{{ $t('Security') }}
					</div>
					<div class="security__subtitle">
						{{ $t('Temporarily block my account') }}
					</div>
					<div class="security__item">
						<div class="security__label">
							{{
								$t(
									'If you suspect that your account has been compromised, you can temporarily suspend it. To restore access, please contact our support team.'
								)
							}}
						</div>
						<BaseButton
							class="security__button _btn-bordered"
							type="submit"
							:disabled="isSubmitting"
							@click="blockAccount"
						>
							<span v-if="isSubmitting">{{ $t('Blocking') }}...</span>
							<span v-else>{{ $t('Block account') }}</span>
						</BaseButton>
					</div>
					<div v-if="securityError" class="personal-info__error">
						{{ securityError }}
					</div>
					<div v-if="securitySuccess" class="personal-info__success">
						{{ securitySuccess }}
					</div>
					<div class="security__item">
						<div class="security__text">
							{{ $t('Delete my account') }}
						</div>
						<BaseButton
							class="security__button _btn-bordered"
							type="submit"
							:disabled="isSubmitting"
							@click="deleteAccount"
						>
							<span v-if="isSubmitting">{{ $t('Deleting') }}...</span>
							<span v-else>{{ $t('Delete account') }}</span>
						</BaseButton>
					</div>
				</div>
			</div>
			<div class="personal-info__column">
				<div class="personal-info__item">
					<div class="personal-info__title _md">
						{{ $t('Steam Settings') }}
					</div>
					<form
						class="personal-info__form"
						@submit.prevent="openSteamProfile"
					>
						<BaseInput
							class="personal-info__input"
							v-model="steamInfo.steamId"
							type="email"
							:label="`${$t('Steam ID')}:`"
							disabled
						/>
						<div class="personal-info__wrapper">
							<BaseButton
								class="personal-info__submit _btn-bordered"
								type="submit"
								:disabled="
									isSubmitting ||
									!steamInfo.steamId ||
									!steamInfo.steamProfileUrl
								"
							>
								<span v-if="isSubmitting">{{ $t('Opening') }}...</span>
								<span v-else>{{ $t('Open steam profile') }}</span>
							</BaseButton>
						</div>
					</form>
					<form
						class="personal-info__form"
						@submit.prevent="updateSteamTradeLink"
					>
						<BaseInput
							class="personal-info__input"
							v-model="steamInfo.steamTradeLink"
							type="text"
							:label="`${$t('Trade link')}:`"
						/>
						<div class="personal-info__wrapper">
							<div v-if="steamError" class="personal-info__error">
								{{ steamError }}
							</div>
							<div v-if="steamSuccess" class="personal-info__success">
								{{ steamSuccess }}
							</div>
							<BaseButton
								class="personal-info__submit _btn-bordered"
								type="submit"
								:disabled="isSubmitting || !steamInfo.steamTradeLink"
							>
								<span v-if="isSubmitting">{{ $t('Changing') }}...</span>
								<span v-else>{{ $t('Change trade link') }}</span>
							</BaseButton>
						</div>
					</form>
					<form class="personal-info__form">
						<BaseInput
							class="personal-info__input"
							v-model="steamInfo.communityVisibilityState"
							type="text"
							:label="`${$t('Profile status')}:`"
							disabled
						/>
						<div class="personal-info__wrapper"></div>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import ChangePassword from '../tabs/ChangePassword.vue';

export default {
	name: 'PersonalInformation',
	components: {
		ChangePassword,
		BaseInput,
		BaseButton,
	},
	data() {
		return {
			isSubmitting: false,
			personalInfo: {
				email: '',
			},
			steamInfo: {
				steamId: '',
				steamProfileUrl: '',
				steamTradeLink: '',
				communityVisibilityState: '',
			},
			personalInfoError: null,
			personalInfoSuccess: null,
			steamError: null,
			steamSuccess: null,
			securityError: null,
			securitySuccess: null,
		};
	},
	watch: {
		'steamInfo.steamTradeLink'(newVal) {
			if (!newVal) {
				this.steamInfo.steamId = '';
				this.steamInfo.steamProfileUrl = '';
				return;
			}

			let partner = null;

			try {
				const url = new URL(newVal);
				partner = url.searchParams.get('partner');
			} catch (e) {
				const match = newVal.match(/[?&]partner=(\d+)/);
				partner = match ? match[1] : null;
			}

			if (!partner) {
				this.steamInfo.steamId = '';
				this.steamInfo.steamProfileUrl = '';
				return;
			}

			const accountId = Number(partner);
			if (!Number.isFinite(accountId) || accountId <= 0) {
				this.steamInfo.steamId = '';
				this.steamInfo.steamProfileUrl = '';
				return;
			}

			const steamId64 = this.addBigIntStrings(
				String(accountId),
				'76561197960265728'
			);

			this.steamInfo.steamId = steamId64;
			this.steamInfo.steamProfileUrl = `https://steamcommunity.com/profiles/${steamId64}`;
		},
	},
	mounted() {
		this.isSubmitting = false;
		console.log('this.steamInfo.profileStatus', this.steamInfo.profileStatus);
	},
	methods: {
		clearSecurityMessages() {
			setTimeout(() => {
				this.securitySuccess = null;
				this.securityError = null;
			}, 3000);
		},
		addBigIntStrings(a, b) {
			a = String(a).replace(/^0+/, '') || '0';
			b = String(b).replace(/^0+/, '') || '0';

			let i = a.length - 1;
			let j = b.length - 1;
			let carry = 0;
			let result = '';

			while (i >= 0 || j >= 0 || carry) {
				const da = i >= 0 ? a.charCodeAt(i) - 48 : 0;
				const db = j >= 0 ? b.charCodeAt(j) - 48 : 0;

				let sum = da + db + carry;
				if (sum >= 10) {
					carry = 1;
					sum -= 10;
				} else {
					carry = 0;
				}

				result = String(sum) + result;
				i--;
				j--;
			}

			return result.replace(/^0+/, '') || '0';
		},
		async blockAccount() {
			this.securityError = null;
			this.securitySuccess = null;
			this.isSubmitting = true;

			try {
				const response = await this.$http.post(
					process.env.VUE_APP_API + 'user/block'
				);

				const status = response.data?.status;
				const message =
					response.data?.message ||
					this.$t('Your account has been blocked.');

				if (status === 'success' || status === 'OK') {
					this.securitySuccess = message;
					this.clearSecurityMessages();
					this.$emit('logout');
				} else {
					this.securityError =
						response.data?.message ||
						this.$t(
							'An unexpected error occurred while blocking account.'
						);
				}
			} catch (error) {
				console.error('Error blocking account:', error);
				this.securityError =
					error.response?.data?.message ||
					this.$t('Error blocking account.');
			} finally {
				this.isSubmitting = false;
			}
		},
		async deleteAccount() {
			this.securityError = null;
			this.securitySuccess = null;
			this.isSubmitting = true;

			try {
				const response = await this.$http.delete(
					process.env.VUE_APP_API + 'user/delete'
				);

				const status = response.data?.status;
				const message =
					response.data?.message ||
					this.$t('Your account has been deleted.');

				if (status === 'success' || status === 'OK') {
					this.securitySuccess = message;
					this.clearSecurityMessages();
					this.$emit('logout');
				} else {
					this.securityError =
						response.data?.message ||
						this.$t(
							'An unexpected error occurred while deleting account.'
						);
					this.clearSecurityMessages();
				}
			} catch (error) {
				console.error('Error deleting account:', error);
				this.securityError =
					error.response?.data?.message ||
					this.$t('Error deleting account.');
				this.clearSecurityMessages();
			} finally {
				this.isSubmitting = false;
			}
		},
		updateEmail() {
			this.personalInfoError = null;
			this.personalInfoSuccess = null;

			const email = this.personalInfo.email?.trim();

			if (!email) {
				this.personalInfoError = 'Please enter your email.';
				return;
			}

			this.isSubmitting = true;

			this.$http
				.post(process.env.VUE_APP_API + 'user/change-email', {
					email,
				})
				.then((response) => {
					if (response.data?.status === 'OK') {
						this.personalInfoSuccess = 'Email updated successfully.';
						this.personalInfoError = '';

						setTimeout(() => {
							this.personalInfoSuccess = '';
						}, 2000);
					} else {
						this.personalInfoSuccess = '';
						this.personalInfoError =
							response.data.message || 'An unexpected error occurred.';
						setTimeout(() => {
							this.personalInfoError = '';
						}, 2000);
					}
				})
				.catch((error) => {
					this.personalInfoError =
						error.response?.data?.message || 'Error updating email.';
					setTimeout(() => {
						this.personalInfoError = '';
					}, 2000);
					console.error('Error updating email:', error);
				})
				.finally(() => {
					this.isSubmitting = false;
				});
		},

		async updateSteamTradeLink() {
			this.steamError = null;
			this.steamSuccess = null;

			const link = this.steamInfo.steamTradeLink?.trim();

			if (!link) {
				this.steamError = 'Please enter your Steam trade link.';
				return;
			}

			this.isSubmitting = true;

			try {
				const response = await this.$http.put(
					process.env.VUE_APP_API + 'user/update-trade-link',
					{ url: link }
				);

				if (response.data?.status === 'OK') {
					this.steamSuccess = 'Trade link updated successfully.';
					setTimeout(() => (this.steamSuccess = ''), 2000);
				} else {
					this.steamError =
						response.data.message || 'An unexpected error occurred.';
					setTimeout(() => (this.steamError = ''), 2000);
				}
			} catch (error) {
				this.steamError =
					error.response?.data?.message || 'Error updating trade link.';
				setTimeout(() => (this.steamError = ''), 2000);
				console.error('Error updating trade link:', error);
			} finally {
				this.isSubmitting = false;
			}
		},

		openSteamProfile() {
			if (this.steamInfo.steamId && this.steamInfo.steamProfileUrl) {
				window.open(this.steamInfo.steamProfileUrl, '_blank');
			} else {
				this.steamError = 'Steam profile URL is not set.';
				setTimeout(() => (this.steamError = ''), 2000);
			}
		},

		async fetchProfile() {
			try {
				const response = await this.$http.get(
					process.env.VUE_APP_API + 'user/profile'
				);

				this.personalInfo.email = response.data.email;

				this.steamInfo.steamId = response.data.steamId;
				this.steamInfo.steamProfileUrl = response.data.steam_profile_url;
				this.steamInfo.steamTradeLink = response.data.steam_trade_link;
				this.steamInfo.communityVisibilityState =
					this.mapCommunityVisibilityState(
						response.data.community_visibility_state
					);
			} catch (error) {
				console.error('Error fetching profile:', error);
			}
		},
		mapCommunityVisibilityState(state) {
			const value = Number(state);

			if (value === 1) {
				return this.$t('Private');
			}

			if (value === 3) {
				return this.$t('Public');
			}

			return this.$t('Unknown');
		},
	},
	created() {
		this.fetchProfile();
	},
};
</script>

<style lang="scss" scoped>
@use '@/assets/styles/mixins' as *;
@use '@/assets/styles/media' as *;
@use '@/assets/styles/components/classes' as *;

.personal-info {
	&__body {
		@media (min-width: 649.98px) {
			display: flex;
			gap: 20px;
		}
	}
	&__column {
		flex: 1 1 100%;
		@media (max-width: 649.98px) {
			&:not(:last-child) {
				@include adaptiveValue('margin-bottom', 35, 20);
			}
		}
	}
	&__title {
		font-family: var(--font-family-inter);
		font-weight: 500;
		line-height: 120%;
		text-transform: capitalize;
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 18, 15);
		}
	}
	&__form {
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 15, 10);
		}
		@media (min-width: $md2) {
			display: flex;
			gap: 20px;
		}
	}
	&__input {
		@media (min-width: $md2) {
			flex: 1 1 100%;
		}
		@media (max-width: $md2) {
			&:not(:last-child) {
				@include adaptiveValue('margin-bottom', 15, 10);
			}
		}
	}
	&__wrapper {
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		@media (min-width: $md2) {
			flex: 1 1 100%;
			max-width: 250px;
		}
	}
	&__error,
	&__success {
		font-family: var(--font-family-inter);
		font-size: 16px;
		line-height: 120%;
		font-weight: 500;
		display: block;
		text-align: center;
		margin-bottom: 8px;
	}
	&__error {
		color: var(--error-color);
	}
	&__success {
		color: var(--success-color);
	}
	&__submit {
		width: 100%;
		align-self: flex-end;
		line-height: 0;
		span {
			line-height: 120%;
			display: flex;
			align-items: center;
			min-height: 100%;
		}
	}
	&__main {
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 35, 20);
		}
	}
	&__security {
	}
}

.security {
	&__title {
		font-family: var(--font-family-inter);
		font-weight: 500;
		line-height: 120%;
		text-transform: capitalize;
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 18, 15);
		}
	}
	&__item {
		@media (min-width: $md2) {
			display: flex;
			gap: 20px;
			&:not(:last-child) {
				@include adaptiveValue('margin-bottom', 30, 15);
			}
		}
		@media (max-width: $md2) {
			&:not(:last-child) {
				@include adaptiveValue('margin-bottom', 15, 10);
			}
		}
	}
	&__subtitle,
	&__label,
	&__text {
		font-family: var(--font-family-inter);
		font-weight: 500;
		line-height: 150%;
		font-size: 16px;
		@media (max-width: $md2) {
			&:not(:last-child) {
				@include adaptiveValue('margin-bottom', 15, 10);
			}
		}
	}
	&__subtitle {
		&:not(:last-child) {
			margin-bottom: 10px;
		}
	}
	&__label {
		color: var(--secondary-color);
		flex: 1 1 100%;
	}
	&__button {
		@media (min-width: $md2) {
			height: fit-content;
			flex: 1 1 100%;
			max-width: 250px;
			align-self: flex-end;
		}
		@media (max-width: $md2) {
			width: 100%;
		}
	}
	&__text {
		flex: 1 1 100%;
	}
}
</style>
